import { parseToE164Phone } from "./phoneE164";

export type NairaWalletFieldType = "date" | "select" | "searchableSelect" | "phone";

export type NairaWalletFieldDefinition = {
  apiKey: string;
  normalizedKey: "dateOfBirth" | "gender" | "stateOfResidence" | "phoneNumber";
  type: NairaWalletFieldType;
  label: string;
  placeholder?: string;
  options?: readonly string[];
};

const FIELD_ALIASES: Record<string, NairaWalletFieldDefinition["normalizedKey"]> =
  {
    dateofbirth: "dateOfBirth",
    date_of_birth: "dateOfBirth",
    dob: "dateOfBirth",
    gender: "gender",
    sex: "gender",
    stateofresidence: "stateOfResidence",
    state_of_residence: "stateOfResidence",
    state: "stateOfResidence",
    residence_state: "stateOfResidence",
    phonenumber: "phoneNumber",
    phone_number: "phoneNumber",
    phone: "phoneNumber",
    mobile: "phoneNumber",
    mobile_number: "phoneNumber",
  };

export const normalizeNairaWalletFieldKey = (
  field: string
): NairaWalletFieldDefinition["normalizedKey"] | string => {
  const compact = field.trim().toLowerCase().replace(/[\s-]+/g, "_");
  return FIELD_ALIASES[compact] ?? field;
};

export const parseRequiredFields = (
  requiredFields: unknown[] | undefined
): string[] => {
  if (!Array.isArray(requiredFields)) return [];

  return requiredFields
    .map((field) => {
      if (typeof field === "string") return field;
      if (field && typeof field === "object" && "key" in field) {
        return String((field as { key: string }).key);
      }
      if (field && typeof field === "object" && "name" in field) {
        return String((field as { name: string }).name);
      }
      return "";
    })
    .filter(Boolean);
};

const isKnownField = (
  key: string
): key is NairaWalletFieldDefinition["normalizedKey"] =>
  key === "dateOfBirth" ||
  key === "gender" ||
  key === "stateOfResidence" ||
  key === "phoneNumber";

export const getNairaWalletFieldDefinition = (
  apiKey: string
): NairaWalletFieldDefinition | null => {
  const normalized = normalizeNairaWalletFieldKey(apiKey);
  if (!isKnownField(normalized)) return null;

  switch (normalized) {
    case "dateOfBirth":
      return {
        apiKey,
        normalizedKey: "dateOfBirth",
        type: "date",
        label: "Date of Birth",
        placeholder: "yyyy-mm-dd",
      };
    case "gender":
      return {
        apiKey,
        normalizedKey: "gender",
        type: "select",
        label: "Select Gender",
        // Values the API documents; the select capitalises them for display.
        options: ["male", "female"],
      };
    case "stateOfResidence":
      return {
        apiKey,
        normalizedKey: "stateOfResidence",
        type: "searchableSelect",
        label: "State of Residence",
      };
    case "phoneNumber":
      return {
        apiKey,
        normalizedKey: "phoneNumber",
        type: "phone",
        label: "Phone Number",
        placeholder: "e.g. +2348012345678 or 08012345678",
      };
    default:
      return null;
  }
};

export const buildNairaWalletFieldDefinitions = (
  requiredFields: unknown[] | undefined
): NairaWalletFieldDefinition[] =>
  parseRequiredFields(requiredFields)
    .map((key) => getNairaWalletFieldDefinition(key))
    .filter((field): field is NairaWalletFieldDefinition => field !== null);

/** Payload keys match backend: dateOfBirth, gender, stateOfResidence, phoneNumber */
export const buildNairaWalletPayload = (
  fieldDefinitions: NairaWalletFieldDefinition[],
  formValues: Record<string, string>
): Record<string, string> => {
  if (fieldDefinitions.length === 0) return {};

  return fieldDefinitions.reduce<Record<string, string>>((acc, field) => {
    const value = formValues[field.apiKey]?.trim();
    if (!value) return acc;

    if (field.normalizedKey === "phoneNumber") {
      const e164 = parseToE164Phone(value);
      if (e164) acc.phoneNumber = e164;
      return acc;
    }

    acc[field.normalizedKey] = value;
    return acc;
  }, {});
};
